import React from 'react';
import Icon from '../../../components/AppIcon';

const InvoiceDetailModal = ({ invoice, onClose }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase()
    })?.format(amount / 100);
  };

  const getStatusColor = (status) => {
    const colors = {
      succeeded: 'text-green-600 bg-green-50',
      pending: 'text-yellow-600 bg-yellow-50',
      failed: 'text-red-600 bg-red-50'
    };
    return colors?.[status] || colors?.pending;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Invoice Details</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Invoice #{invoice?.stripeInvoiceId?.substring(0, 12) || 'N/A'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-6">
            {/* Status and Date */}
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600 mb-1">Payment Date</p>
                <p className="text-base font-medium text-gray-900">
                  {formatDate(invoice?.createdAt)}
                </p>
              </div>
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice?.status)}`}>
                  {invoice?.status?.charAt(0)?.toUpperCase() + invoice?.status?.slice(1)}
                </span>
              </div>
            </div>

            {/* Line Items */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Items</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {invoice?.description || 'Pro Subscription'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Monthly subscription fee
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatAmount(invoice?.amount, invoice?.currency)}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">
                  {formatAmount(invoice?.amount, invoice?.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Tax</span>
                <span className="text-gray-900">$0.00</span>
              </div>
              <div className="flex items-center justify-between text-base font-semibold pt-3 border-t border-gray-200">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">
                  {formatAmount(invoice?.amount, invoice?.currency)}
                </span>
              </div>
            </div>

            {/* Payment Information */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-1">Payment Information</p>
                  <p className="text-xs text-blue-700">
                    Payment ID: {invoice?.stripePaymentIntentId?.substring(0, 20) || 'N/A'}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    Processed securely through Stripe
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Icon name="Download" className="w-4 h-4" />
              Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;